import React, { useState, useRef, useEffect } from 'react';
import { TextInput, Platform, View, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import i18n from 'i18n-js';
import { handleBarcodeInput, setBarcodeInput, processPickCompleteBarcode, setMainInputFocus } from '../../actions/ScanToCartScanningActions';
import styles from '../../style/scanpack';
import audioService from '../../services/AudioService';
import useAutoFocus, { useNavigationFocus } from './hooks/useAutoFocus';
import useKeyboardSuppression, {getKeyboardSuppressionProps} from '../../hooks/useKeyboardSuppression';
import { Ionicons } from '@expo/vector-icons';
import useEnterKeySubmit from '../../commons/hooks/useEnterKeySubmit';

const BarcodeInput = ({windowWidth, camScannerEnabled, onCameraOpen}) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [placeholder, setPlaceholder] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Get all needed data from Redux
  const { barcodeValue, mainInputHasFocus, pickComplete } = useSelector(state => ({
    barcodeValue: state.scanToCartScanning.barcodeValue,
    mainInputHasFocus: state.scanToCartScanning.mainInputHasFocus,
    pickComplete: state.scanToCartScanning.cartScanComplete,
  }));

  const suppressSoftwareKeyboard = useKeyboardSuppression(inputRef);

  // Enable auto-focus functionality with 2 second delay
  useAutoFocus(2000);

  // Restore focus when navigating back to the screen
  useNavigationFocus();

  // Initialize placeholder on mount
  useEffect(() => {
    const initialPlaceholder = getPlaceHolder();
    setPlaceholder(initialPlaceholder);
  }, []);

  // Handle focus/blur based on Redux state
  useEffect(() => {
    if (mainInputHasFocus) {
      inputRef.current?.focus();
    } else {
      inputRef.current?.blur();
    }
  }, [mainInputHasFocus]);

  const handleBlur = () => {
    const blurValue = getPlaceHolder();
    setPlaceholder(blurValue);
    dispatch(setMainInputFocus(false));
  };

  const getPlaceHolder = () => {
    return Platform.OS === 'web'
      ? i18n.t("nextItems.clickScanLabel") || "Click to Scan"
      : i18n.t("nextItems.tabHereLabel") || "Tab Here";
  }

  const handleFocus = () => {
    let focusValue;
    if (pickComplete) {
      focusValue = i18n.t("nextItems.scanToteToContinue");
    } else {
      focusValue = i18n.t("nextItems.readyForScanLabel");
    }
    setPlaceholder(focusValue);
    dispatch(setMainInputFocus(true));
  };

  const handleScanBarcode = async () => {
    if(barcodeValue === ''){
      await audioService.playNotReadySound();
      return;
    }
    if(pickComplete){
      dispatch(processPickCompleteBarcode(barcodeValue, setIsLoading));
      return;
    }
    dispatch(handleBarcodeInput());
  };

  const handleBarcodeChange = (text) => {
    dispatch(setBarcodeInput(text));
  };

  const { onKeyPress, onSubmitEditing } = useEnterKeySubmit(handleScanBarcode);

  return (
    <View style={{ position: 'relative' }}>
      <TextInput
        testID="barcode-input"
        value={barcodeValue}
        ref={inputRef}
        style={[styles.scanBarCodeInput,{fontSize: windowWidth < 900 ? 16 : 22}]}
        {...getKeyboardSuppressionProps(suppressSoftwareKeyboard)}
        onSubmitEditing={onSubmitEditing}
        onKeyPress={onKeyPress}
        onChangeText={handleBarcodeChange}
        placeholder={placeholder}
        placeholderTextColor={'#acacad'}
        onBlur={handleBlur}
        onFocus={handleFocus}
        autoCapitalize='none'
        returnKeyType='done'
        editable={!isLoading}
      />
      {camScannerEnabled && (
        <TouchableOpacity
          testID="cameraScanBtn"
          style={{
            position: 'absolute',
            right: 10,
            top: 0,
            bottom: 0,
            justifyContent: 'center',
          }}
          onPress={onCameraOpen}>
          <Ionicons
            name="camera"
            size={24}
            color="#acacad"
          />
        </TouchableOpacity>
      )}
      {isLoading && (
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 5
        }}>
          <ActivityIndicator size="small" color="#007AFF" />
        </View>
      )}
    </View>
  );
};

export default BarcodeInput;
